import { useId, type ReactNode } from 'react'

import { cx } from '../lib/cx'
import { Button } from './Button'
import { Icon } from './Icon'
import { useDialogFocus } from './useDialogFocus'

/**
 * Diálogo centrado: cargar un dato a mano, crear un seguimiento en Plane.
 *
 * `error` + `onRetry` implementan el camino de error obligatorio de la spec: si
 * la escritura en el sistema de origen falla, el diálogo **no se cierra** y
 * ofrece Reintentar con los datos intactos. Un seguimiento que existe en el
 * panel y no en Plane es un pendiente que nadie va a ver nunca.
 */
export interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  /** Bajada bajo el título. */
  subtitle?: ReactNode
  /** Acciones del pie. El primario va último, a la derecha. */
  footer?: ReactNode
  /** Mensaje de error. Se renderiza con `role="alert"` arriba del pie. */
  error?: string | null
  /** Sin este handler el bloque de error no ofrece Reintentar. */
  onRetry?: () => void
  /** Deshabilita Reintentar mientras la operación está en curso. */
  reintentando?: boolean
  size?: 'sm' | 'md'
  children?: ReactNode
}

const ANCHO: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  footer,
  error,
  onRetry,
  reintentando,
  size = 'md',
  children,
}: ModalProps) {
  const tituloId = useId()
  const { contenedorRef, cerrarRef } = useDialogFocus<
    HTMLDivElement,
    HTMLButtonElement
  >(open, onClose)

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
      <div
        data-testid="modal-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-text/25"
      />
      <div
        ref={contenedorRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        data-testid="modal"
        className={cx(
          'relative z-10 w-full overflow-hidden rounded-card border border-border bg-surface shadow-pop',
          ANCHO[size],
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <h2 id={tituloId} className="text-titulo text-text">
              {title}
            </h2>
            {subtitle !== undefined ? (
              <p className="mt-0.5 text-meta text-muted-strong">{subtitle}</p>
            ) : null}
          </div>
          <button
            ref={cerrarRef}
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="shrink-0 rounded p-1 text-muted transition-colors duration-150 ease-brand hover:bg-n100 hover:text-text"
          >
            <Icon name="cerrar" size={13} />
          </button>
        </header>

        <div className="flex flex-col gap-3 px-4 py-4">
          {children}

          {error !== null && error !== undefined && error !== '' ? (
            <div
              role="alert"
              data-testid="modal-error"
              className="flex flex-col gap-2 rounded-md border border-danger border-l-[3px] bg-danger-soft px-3 py-2 text-meta"
            >
              <span className="inline-flex items-start gap-1.5 font-semibold text-danger-text">
                <Icon name="critico" size={13} className="mt-px" />
                {error}
              </span>
              {onRetry !== undefined ? (
                <div>
                  <Button
                    variante="peligro"
                    onClick={onRetry}
                    disabled={reintentando === true}
                  >
                    Reintentar
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {footer !== undefined ? (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-n100 px-4 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  )
}
